import React, { useEffect, useMemo, useState } from 'react';
import { confirmAction } from '../utils/confirm';
import {
    Setpoint, SetpointParameter,
    fetchSetpoints, fetchSetpointParameters, createSetpoint, updateSetpoint, deleteSetpoint,
    fetchDevices,
} from '../services/api';
import { Device } from '../types';
import ErrorBanner from './shared/ErrorBanner';
import PageHeader from './shared/PageHeader';
import { ui } from '../styles/shared';
import { useAuth } from '../context/AuthContext';

// Stilar från den gemensamma stilfilen (kompakta fält i tabellen)
const inp: React.CSSProperties = { ...ui.input, width: undefined, padding: '6px 8px', fontSize: 13 };
const btn = (bg: string): React.CSSProperties => ui.btnSmall(bg);

const Setpoints: React.FC = () => {
    const { isReadOnly } = useAuth();
    const [setpoints, setSetpoints] = useState<Setpoint[]>([]);
    const [devices, setDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Add form
    const [showAdd, setShowAdd] = useState(false);
    const [addIeee, setAddIeee] = useState('');
    const [addParams, setAddParams] = useState<SetpointParameter[]>([]);
    const [addParamId, setAddParamId] = useState<number | ''>('');
    const [addTarget, setAddTarget] = useState('');
    const [addUnit, setAddUnit] = useState('');
    const [saving, setSaving] = useState(false);

    // Inline edit
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editTarget, setEditTarget] = useState('');
    const [editUnit, setEditUnit] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [sp, dv] = await Promise.all([fetchSetpoints(), fetchDevices()]);
            setSetpoints(sp);
            setDevices(dv);
            setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte ladda börvärden');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // When the device changes in the add form, load its properties.
    useEffect(() => {
        if (!addIeee) { setAddParams([]); setAddParamId(''); return; }
        let cancelled = false;
        (async () => {
            try {
                const params = await fetchSetpointParameters(addIeee);
                if (!cancelled) { setAddParams(params); setAddParamId(''); }
            } catch (e: any) {
                if (!cancelled) setError(e.message || 'Kunde inte ladda parametrar');
            }
        })();
        return () => { cancelled = true; };
    }, [addIeee]);

    // Prefill unit from the selected parameter's metadata.
    useEffect(() => {
        const p = addParams.find(x => x.id === addParamId);
        if (p && p.parameter_unit) setAddUnit(p.parameter_unit);
    }, [addParamId, addParams]);

    const deviceOptions = useMemo(
        () => devices.filter(d => d.ieee_address).sort((a, b) => a.name.localeCompare(b.name)),
        [devices]
    );

    const handleAdd = async () => {
        const target = Number(addTarget.replace(',', '.'));
        if (addParamId === '' || !Number.isFinite(target)) {
            setError('Välj en property och ange ett numeriskt börvärde');
            return;
        }
        setSaving(true);
        try {
            await createSetpoint({ parameter_id: Number(addParamId), target_value: target, unit: addUnit.trim() || null });
            setShowAdd(false);
            setAddIeee(''); setAddParamId(''); setAddTarget(''); setAddUnit('');
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte spara börvärde'); }
        finally { setSaving(false); }
    };

    const startEdit = (sp: Setpoint) => {
        setEditingId(sp.id);
        setEditTarget(String(sp.target_value));
        setEditUnit(sp.unit ?? '');
    };

    const handleEditSave = async () => {
        if (editingId === null) return;
        const target = Number(editTarget.replace(',', '.'));
        if (!Number.isFinite(target)) { setError('Ange ett numeriskt börvärde'); return; }
        try {
            await updateSetpoint(editingId, { target_value: target, unit: editUnit.trim() || null });
            setEditingId(null);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte uppdatera'); }
    };

    const handleDelete = async (sp: Setpoint) => {
        if (!confirmAction(`Ta bort börvärdet för ${sp.device_name} · ${sp.parameter_name || sp.parameter_key}?`)) return;
        try {
            await deleteSetpoint(sp.id);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte ta bort'); }
    };

    const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #dee2e6' };
    const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' };

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 16px' }}>
            <PageHeader icon="🎯" title="Börvärden" description="Ange ett önskat värde per givare och mätvärde. Skillnaden mot börvärdet visas sedan bredvid värdena på översikten och i bevakningen." />

            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {loading ? (
                <div style={{ color: '#888', fontSize: 13 }}>Laddar…</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' }}>
                    <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                            <th style={th}>Enhet (device)</th>
                            <th style={th}>Property</th>
                            <th style={th}>Börvärde</th>
                            <th style={th}>Enhet</th>
                            <th style={th}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {setpoints.length === 0 && (
                            <tr><td style={td} colSpan={5}><span style={{ color: '#888' }}>Inga börvärden registrerade ännu.</span></td></tr>
                        )}
                        {setpoints.map(sp => editingId === sp.id ? (
                            <tr key={sp.id} style={{ background: '#fff9e6' }}>
                                <td style={td}>{sp.device_name}</td>
                                <td style={td}>{sp.parameter_name || sp.parameter_key}</td>
                                <td style={td}>
                                    <input style={{ ...inp, width: 90 }} value={editTarget}
                                        onChange={e => setEditTarget(e.target.value)} />
                                </td>
                                <td style={td}>
                                    <input style={{ ...inp, width: 70 }} value={editUnit}
                                        onChange={e => setEditUnit(e.target.value)} />
                                </td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                    <button onClick={handleEditSave} style={{ ...btn('#28a745'), marginRight: 6 }}>Spara</button>
                                    <button onClick={() => setEditingId(null)} style={btn('#6c757d')}>Avbryt</button>
                                </td>
                            </tr>
                        ) : (
                            <tr key={sp.id}>
                                <td style={td}>{sp.device_name}</td>
                                <td style={td}>
                                    {sp.parameter_name || sp.parameter_key}
                                    {sp.parameter_name && (
                                        <span style={{ color: '#aaa', fontFamily: 'monospace', fontSize: 11, marginLeft: 6 }}>{sp.parameter_key}</span>
                                    )}
                                </td>
                                <td style={{ ...td, fontWeight: 700, fontFamily: 'monospace' }}>{sp.target_value}</td>
                                <td style={{ ...td, color: '#888' }}>{sp.unit || sp.parameter_unit || ''}</td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                    {!isReadOnly && (
                                        <>
                                            <button onClick={() => startEdit(sp)} style={{ ...btn('#007bff'), marginRight: 6 }}>Ändra</button>
                                            <button onClick={() => handleDelete(sp)} style={btn('#dc3545')}>Ta bort</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!isReadOnly && (showAdd ? (
                <div style={{ marginTop: 16, padding: 14, background: '#f8fcff', border: '1px solid #dee2e6', borderRadius: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Nytt börvärde</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'end' }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 3 }}>Enhet (device)</label>
                            <select style={{ ...inp, width: '100%' }} value={addIeee} onChange={e => setAddIeee(e.target.value)}>
                                <option value="">— välj —</option>
                                {deviceOptions.map(d => (
                                    <option key={d.ieee_address} value={d.ieee_address}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 3 }}>Property</label>
                            <select style={{ ...inp, width: '100%' }} value={addParamId}
                                disabled={!addIeee}
                                onChange={e => setAddParamId(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">— välj —</option>
                                {addParams.map(p => (
                                    <option key={p.id} value={p.id}>{p.parameter_name || p.parameter_key}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 3 }}>Börvärde</label>
                            <input style={{ ...inp, width: '100%' }} value={addTarget} placeholder="-18"
                                onChange={e => setAddTarget(e.target.value)} />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 3 }}>Enhet</label>
                            <input style={{ ...inp, width: '100%' }} value={addUnit} placeholder="°C"
                                onChange={e => setAddUnit(e.target.value)} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button onClick={handleAdd} disabled={saving} style={btn('#28a745')}>{saving ? 'Sparar…' : 'Lägg till'}</button>
                        <button onClick={() => setShowAdd(false)} style={btn('#6c757d')}>Avbryt</button>
                    </div>
                </div>
            ) : (
                <button onClick={() => setShowAdd(true)} style={{ ...btn('#007bff'), marginTop: 14 }}>+ Lägg till börvärde</button>
            ))}
        </div>
    );
};

export default Setpoints;
